import {
  DashboardCard,
  DashboardLayouts,
  GridCardLayouts,
  CardMetadata,
} from './types';

import * as _ from 'lodash';

export const guidGenerator = () => {
  var S4 = function() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (
    S4() +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    S4() +
    S4()
  );
};

export const saveToLS = (
  name: string,
  scope: string,
  key: string,
  value: {}
) => {
  if (window.localStorage) {
    window.localStorage.setItem(
      `${name}_${scope}`,
      JSON.stringify({
        [key]: value,
      })
    );
  }
};

export const getFromLS = (name: string, scope: any, key: any) => {
  let ls: any = {};
  if (window.localStorage) {
    try {
      ls = JSON.parse(window.localStorage.getItem(`${name}_${scope}`)!) || {};
    } catch (e) {
      /*Ignore*/
    }
  }
  return ls[key];
};

export const getChartOptions = () => {
  return {
    legend: {
      display: true,
      labels: {
        padding: 2,
        boxWidth: 20,
      },
    },

    layout: {
      padding: 5,
    },
    responsive: true,
    maintainAspectRatio: true,
  };
};

/**
 * Remove the extra attributes from the layouts
 * so that is can be used for unique entries
 * @param layouts
 */
const normalizeLayouts = (layouts: GridCardLayouts) => {
  let finalLayouts = {
    lg: [],
    md: [],
    sm: [],
    xs: [],
    xxs: [],
  } as GridCardLayouts;

  Object.entries(layouts).map(data => {
    const key = data[0];
    const value = data[1];
    value.map((item: any) => {
      const dataValue = {
        w: item.w,
        h: item.h,
        x: item.x,
        y: item.y,
        static: item.static ? item.static : false,
        isDraggable: item.static ? false : true,
        isResizable: item.static ? false : true,
        cardId: item.cardId,
      };

      switch (key) {
        case 'lg':
          finalLayouts.lg.push(dataValue);
          break;
        case 'md':
          finalLayouts.md.push(dataValue);
          break;
        case 'sm':
          finalLayouts.sm.push(dataValue);
          break;
        case 'xs':
          finalLayouts.xs.push(dataValue);
          break;
        case 'xxs':
          finalLayouts.xxs.push(dataValue);
          break;
        default:
          break;
      }
    });
  });

  return finalLayouts;
};

/**
 * Take the user defined card layouts and transform to what the dashboard understands
 * If there are already cards defined , it will merge them together and return
 * the cards and layouts
 * @param dashboardCardLayouts
 * @param baselineLayouts
 * @param baselineCards
 */
export const transformToDashboardConfig = (
  dashboardCardLayouts: DashboardLayouts,
  baselineLayouts?: GridCardLayouts,
  baselineCards?: CardMetadata[]
) => {
  let cards: any[] = [];

  if (
    dashboardCardLayouts !== null &&
    typeof dashboardCardLayouts.config !== 'undefined' &&
    dashboardCardLayouts.config
  ) {
    cards = dashboardCardLayouts.config.map((c: DashboardCard) => {
      const { metadata } = c.card;
      return metadata;
    });
  }

  if (typeof baselineCards !== 'undefined' && baselineCards.length > 0) {
    cards = [...cards, ...baselineCards];
  }

  cards = _.uniqBy(cards, 'cardId');
  let finalLayouts = {
    lg: [],
    md: [],
    sm: [],
    xs: [],
    xxs: [],
  } as GridCardLayouts;

  if (typeof baselineLayouts !== 'undefined') {
    const finalBaseLineLayouts = normalizeLayouts(baselineLayouts);
    finalLayouts = {
      ...finalLayouts,
      lg: finalBaseLineLayouts.lg
        ? [...finalLayouts.lg, ...finalBaseLineLayouts.lg]
        : [...finalLayouts.lg],
      md: finalBaseLineLayouts.md
        ? [...finalLayouts.md, ...finalBaseLineLayouts.md]
        : [...finalLayouts.md],
      sm: finalBaseLineLayouts.sm
        ? [...finalLayouts.sm, ...finalBaseLineLayouts.sm]
        : [...finalLayouts.sm],
      xs: finalBaseLineLayouts.xs
        ? [...finalLayouts.xs, ...finalBaseLineLayouts.xs]
        : [...finalLayouts.xs],
      xxs: finalBaseLineLayouts.xxs
        ? [...finalLayouts.xxs, ...finalBaseLineLayouts.xxs]
        : [...finalLayouts.xxs],
    };
  }
  if (
    dashboardCardLayouts !== null &&
    typeof dashboardCardLayouts.config !== 'undefined' &&
    dashboardCardLayouts.config
  ) {
    dashboardCardLayouts.config.map((c: DashboardCard) => {
      const { metadata, layout } = c.card;
      Object.entries(layout).map(data => {
        const key = data[0];
        const value = data[1];
        const cardId: string =
          metadata && typeof metadata.cardId === 'string'
            ? metadata.cardId
            : '';
        const dataValue = { ...value, cardId };
        switch (key) {
          case 'lg':
            finalLayouts.lg.push(dataValue);
            finalLayouts.lg = _.uniqBy([...finalLayouts.lg], 'cardId');
            break;
          case 'md':
            finalLayouts.md.push(dataValue);
            finalLayouts.md = _.uniqBy([...finalLayouts.md], 'cardId');
            break;
          case 'sm':
            finalLayouts.sm.push(dataValue);
            finalLayouts.sm = _.uniqBy([...finalLayouts.sm], 'cardId');
            break;
          case 'xs':
            finalLayouts.xs.push(dataValue);
            finalLayouts.xs = _.uniqBy([...finalLayouts.xs], 'cardId');
            break;
          case 'xxs':
            finalLayouts.xxs.push(dataValue);
            finalLayouts.xxs = _.uniqBy([...finalLayouts.xxs], 'cardId');
            break;
          default:
            break;
        }
      });
    });
  }

  return { cards, layouts: finalLayouts };
};

/**
 * Download the dashboard config as JSON
 * @param jsonData
 * @param fileName
 */
export const downloadJSONFile = async (jsonData: any, fileName: string) => {
  const json = JSON.stringify(jsonData);
  const blob = new Blob([json], { type: 'application/json' });
  const href = await URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName + '.json';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
